import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const VC_EDITING_QUERY_KEY = ['settings', 'feature', 'verification-contact-editing'];
const VC_WHITELIST_QUERY_KEY = ['settings', 'feature', 'verification-contact-whitelist'];

interface ApiSuccessResponse<T> {
  status: 'success';
  data: T;
}

interface VCEditingResponse {
  enabled: boolean;
}

interface VCWhitelistResponse {
  enabled: boolean;
  emails: string[];
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 500)
      return 'An internal server error occurred. Please try again later';
    if (error.response?.status === 401 || error.response?.status === 403)
      return 'You do not have permission to perform this action';
    if (error.message === 'Network Error')
      return 'Unable to connect to the server. Please check your internet connection';
  }
  return 'An unexpected error occurred. Please try again';
}

export function useVCEditingEnabled() {
  const axios = useAxios();
  return useQuery({
    queryKey: VC_EDITING_QUERY_KEY,
    queryFn: async () => {
      const { data } = await axios.get<ApiSuccessResponse<VCEditingResponse>>(
        '/settings/feature/verification-contact-editing',
      );
      return data.data;
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useSetVCEditingEnabled() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<VCEditingResponse>>(
          '/settings/feature/verification-contact-editing',
          { enabled },
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: VC_EDITING_QUERY_KEY });
      const previous = queryClient.getQueryData<VCEditingResponse>(VC_EDITING_QUERY_KEY);
      queryClient.setQueryData<VCEditingResponse>(VC_EDITING_QUERY_KEY, { enabled });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(VC_EDITING_QUERY_KEY, context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: VC_EDITING_QUERY_KEY });
    },
  });
}

export function useVCWhitelistSettings() {
  const axios = useAxios();
  return useQuery({
    queryKey: VC_WHITELIST_QUERY_KEY,
    queryFn: async () => {
      const { data } = await axios.get<ApiSuccessResponse<VCWhitelistResponse>>(
        '/settings/feature/verification-contact-whitelist',
      );
      return data.data;
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

export function useUpdateVCWhitelist() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (update: { enabled?: boolean; emails?: string[] }) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<VCWhitelistResponse>>(
          '/settings/feature/verification-contact-whitelist',
          update,
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey: VC_WHITELIST_QUERY_KEY });
      const previous = queryClient.getQueryData<VCWhitelistResponse>(VC_WHITELIST_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<VCWhitelistResponse>(VC_WHITELIST_QUERY_KEY, {
          enabled: update.enabled ?? previous.enabled,
          emails: update.emails ?? previous.emails,
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(VC_WHITELIST_QUERY_KEY, context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: VC_WHITELIST_QUERY_KEY });
    },
  });
}
